import { z } from "zod";
import { plainReason } from "../storeSetup/storeSetupModel";
import { billboardName } from "./media";
import { formatUtcPeriod } from "./utcDays";

/**
 * Advertiser notifications from `GET /me/notifications` (ADR-0009 #11): creative approved /
 * rejected / revoked, billboard rental confirmed / started / ended, and a sign replaced after a
 * takeover. Payloads are server-generated; reviewer notes are plain text and are rendered as text,
 * never as markup. Unknown types or malformed payloads are ignored.
 */

export const AD_NOTICE_TYPES = [
  "creative.approved",
  "creative.rejected",
  "creative.revoked",
  "billboard.confirmed",
  "billboard.started",
  "billboard.ended",
  "placement.replaced",
  // Sponsored-task campaigns (M6, ADR-0011): review decisions and the takeover pause.
  "campaign.approved",
  "campaign.rejected",
  "campaign.suspended",
  "campaign.takeover_paused",
  // "Add an offer" on one screen (ADR-0027): the store's offers passed review, were paid for and started by themselves.
  "campaign.live",
  // ADR-0027: the store's website changed: its website offers were renewed for the new address (or ended).
  "campaign.website_renewed",
  // ADR-0030: the owner acquired a store and has earlier offers to re-use there ("Re-use your offers").
  "campaign.reuse_offers",
  // ADR-0031: the owner acquired a store and has their own design (and offers) from an earlier store to re-use there.
  "store.reuse",
  // "Your store" on one screen (ADR-0020): the store-screen design went on the store, or needs a change.
  "store.live",
  "store.needs_change",
] as const;
export type AdNoticeType = (typeof AD_NOTICE_TYPES)[number];

// The server sends null where nothing applies (a placeholder sign has no creative; a rental's title can be null).
const Payload = z
  .object({
    creativeId: z.string().max(64).nullable().optional(),
    title: z.string().max(120).nullable().optional(),
    kind: z.enum(["storefront", "billboard"]).optional(),
    version: z.number().int().optional(),
    note: z.string().max(1000).nullable().optional(),
    reviewNote: z.string().max(1000).nullable().optional(),
    billboardId: z.string().max(16).optional(),
    label: z.string().max(120).optional(),
    startDate: z.string().max(10).optional(),
    endDate: z.string().max(10).optional(),
    propertyId: z.string().max(16).optional(),
    address: z.string().max(120).optional(),
    orderId: z.string().max(64).optional(),
    campaignId: z.string().max(64).optional(),
    /** ADR-0027: offers saved on the store screen (they start by themselves). */
    autoStart: z.boolean().optional(),
    /** ADR-0027 website renewal. */
    newHost: z.string().max(253).nullable().optional(),
    renewed: z.number().int().min(0).optional(),
    ended: z.number().int().min(0).optional(),
    quizMentionsOldSite: z.number().int().min(0).optional(),
    /** ADR-0029: free offers (no payment) or a campaign paid from a budget before offers became free. */
    billing: z.enum(["free", "budget"]).optional(),
    /** ADR-0030 "Re-use your offers": where the owner's earlier offers were. */
    fromAddress: z.string().max(120).optional(),
    fromBusinessName: z.string().max(120).optional(),
    offers: z.number().int().min(0).optional(),
  })
  .loose();

export type AdNoticeTab = "creatives" | "billboards" | "stats" | "tasks";

export interface AdNotice {
  key: string;
  kind: "advertising";
  type: AdNoticeType;
  text: string;
  tone: "info" | "success" | "warning" | "danger";
  at: string | null;
  /** Where "Open" goes in the advertiser dashboard. */
  tab: AdNoticeTab;
  /** What to re-read when it arrives. */
  refresh: { creatives: boolean; billboards: boolean; properties: boolean; stats: boolean; campaigns?: boolean };
  /** An approved store design: the toast offers "Show on my store" (approval alone does not change the sign). */
  showOnStoreCreativeId?: string;
  /** ADR-0020: the store the notice is about; the toast opens its store screen. */
  storePropertyId?: string;
  /** ADR-0027: offers saved on the store's one screen; the toast opens that screen. */
  offersPropertyId?: string;
  /** ADR-0030: the toast opens that screen filled in from the owner's earlier offers ("Re-use your offers"). */
  reuseOffers?: boolean;
  /** ADR-0031: the toast opens the store screen filled in from the owner's earlier design (then the offers). */
  storeReuse?: { offers: number };
}

export function isAdNoticeType(type: string): type is AdNoticeType {
  return (AD_NOTICE_TYPES as readonly string[]).includes(type);
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const quoted = (title: string | null | undefined, fallback = "your design") => (title ? `“${clip(title, 60)}”` : fallback);

export function adNoticeFromNotification(item: { id: string; type: string; payload: unknown; createdAt?: string }): AdNotice | null {
  const type = item.type;
  if (!isAdNoticeType(type)) return null;
  const parsed = Payload.safeParse(item.payload ?? {});
  if (!parsed.success) return null;
  const p = parsed.data;
  const note = (p.note ?? p.reviewNote ?? "").trim();
  const board = p.billboardId ? billboardName(p.billboardId, p.label) : (p.label ?? "your billboard");
  const period = p.startDate && p.endDate ? ` ${formatUtcPeriod(p.startDate, p.endDate)}` : "";
  const base = { key: `n:${item.id}`, kind: "advertising" as const, type, at: item.createdAt ?? null };
  const none = { creatives: false, billboards: false, properties: false, stats: false };
  // ADR-0027: offers saved on the store screen speak of the store's offers and open that screen.
  const offers = p.autoStart === true && p.propertyId ? { offersPropertyId: p.propertyId } : null;
  const at = p.address ? ` at ${p.address}` : "";
  // ADR-0029: free offers have nothing to pay; older paid campaigns kept their budget.
  const free = p.billing === "free";
  if (offers && type === "campaign.approved") {
    return {
      ...base,
      tone: "success",
      tab: "tasks",
      refresh: { ...none, campaigns: true },
      text: free ? "Your offers passed review. Open your offers to see why they are not running yet." : "Your offers passed review. They start by themselves once their budget pays for a completion.",
      ...offers,
    };
  }
  if (offers && type === "campaign.rejected") {
    return { ...base, tone: "warning", tab: "tasks", refresh: { ...none, campaigns: true }, text: `Your offers need a change${note ? `: ${clip(note, 240)}` : "."} Change them and send them again.`, ...offers };
  }
  if (offers && type === "campaign.suspended") {
    return { ...base, tone: "danger", tab: "tasks", refresh: { ...none, campaigns: true }, text: `Townsy stopped your offers${note ? `: ${clip(note, 240)}` : "."} Change them and send them for review again.`, ...offers };
  }
  switch (type) {
    case "creative.approved":
      return {
        ...base,
        tone: "success",
        tab: "creatives",
        refresh: { ...none, creatives: true },
        text: `${quoted(p.title)} was approved.${p.kind === "billboard" ? " You can book a billboard with it now." : p.kind === "storefront" ? " It shows on a store once you choose it there: use Show on my store." : ""}`,
        ...(p.kind === "storefront" && p.creativeId ? { showOnStoreCreativeId: p.creativeId } : {}),
      };
    case "creative.rejected":
      return {
        ...base,
        tone: "warning",
        tab: "creatives",
        refresh: { ...none, creatives: true },
        text: `${quoted(p.title)} was not approved${note ? `: ${clip(note, 240)}` : "."} You can change it and submit a new version.`,
      };
    case "creative.revoked":
      return {
        ...base,
        tone: "danger",
        tab: "creatives",
        refresh: { creatives: true, billboards: true, properties: true, stats: false },
        text: `Townsy revoked the approval of ${quoted(p.title)}${note ? `: ${clip(note, 240)}` : "."} Where it was showing, a neutral placeholder is shown now.`,
      };
    case "billboard.confirmed":
      return { ...base, tone: "success", tab: "billboards", refresh: { ...none, billboards: true, stats: true }, text: `Billboard booked: ${board} shows your design${period}.` };
    case "billboard.started":
      return { ...base, tone: "info", tab: "billboards", refresh: { ...none, billboards: true, stats: true }, text: `Your rental of ${board} has started: it shows your design now.` };
    case "billboard.ended":
      return { ...base, tone: "info", tab: "stats", refresh: { ...none, billboards: true, stats: true }, text: `Your rental of ${board} has ended. Its statistics stay in your dashboard.` };
    case "placement.replaced":
      return {
        ...base,
        tone: "warning",
        tab: "stats",
        refresh: { ...none, properties: true, stats: true },
        text: `Your sign at ${p.address ?? "your former store"} was removed because the store has a new owner. Your past statistics stay in your dashboard.`,
      };
    case "campaign.approved":
      return {
        ...base,
        tone: "success",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: (free
          ? `Your campaign ${quoted(p.title, "")} was approved. Activate it on the Tasks tab to show the tasks in town (free for you: Townsy gives the coins).`
          : `Your campaign ${quoted(p.title, "")} was approved. Activate it on the Tasks tab to show the tasks in town once its budget pays for a completion.`
        ).replace("  ", " "),
      };
    case "campaign.rejected":
      return {
        ...base,
        tone: "warning",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: `Your campaign ${quoted(p.title, "")} was not approved${note ? `: ${clip(note, 240)}` : "."} You can change it and submit it again.`.replace("  ", " "),
      };
    case "campaign.suspended":
      return {
        ...base,
        tone: "danger",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: `Townsy paused your campaign ${quoted(p.title, "")}${note ? `: ${clip(note, 240)}` : "."}${free ? "" : " Its unused budget stays with the campaign."}`.replace("  ", " "),
      };
    case "store.live":
      return {
        ...base,
        tone: "success",
        tab: "creatives",
        refresh: { ...none, creatives: true, properties: true },
        text: `Your store${p.address ? ` at ${p.address}` : ""} is live: everyone in town sees ${quoted(p.title, "your new sign")}.`,
        ...(p.propertyId ? { storePropertyId: p.propertyId } : {}),
      };
    case "store.needs_change":
      return {
        ...base,
        tone: "warning",
        tab: "creatives",
        refresh: { ...none, creatives: true },
        text: `Your store${p.address ? ` at ${p.address}` : ""} needs a change${note ? `: ${clip(plainReason(note), 240)}` : "."} Open your store, fix it and save again.`,
        ...(p.propertyId ? { storePropertyId: p.propertyId } : {}),
      };
    case "campaign.live":
      return {
        ...base,
        tone: "success",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: free
          ? `Your offers${at} are live: they passed review, so they started by themselves. Free for you: Townsy gives the coins.`
          : `Your offers${at} are live: they passed review and are paid for, so they started by themselves.`,
        ...(p.propertyId ? { offersPropertyId: p.propertyId } : {}),
      };
    case "campaign.website_renewed": {
      const quiz = p.quizMentionsOldSite ? ` ${p.quizMentionsOldSite === 1 ? "A question offer mentions" : `${p.quizMentionsOldSite} question offers mention`} your old website: change it if needed.` : "";
      return {
        ...base,
        tone: "info",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: p.newHost
          ? `Your website changed: your website offers were renewed for the new address (${clip(p.newHost, 80)}) and are being checked.${quiz}`
          : `Your store has no website now: your website offers were ended. You can add other offers.${quiz}`,
        ...(p.propertyId ? { offersPropertyId: p.propertyId } : {}),
      };
    }
    case "campaign.takeover_paused":
      return {
        ...base,
        tone: "warning",
        tab: "tasks",
        refresh: { ...none, campaigns: true, properties: true },
        // ADR-0030: the offers vanish with the store; they can be re-used on the owner's next store.
        text: `Your campaign ${quoted(p.title, "")} stopped for good because the store has a new owner: visitors no longer see its offers.${free ? "" : " Its unused budget stays yours."} Its report stays in your dashboard. When you own another store, you can re-use its offers there.`.replace("  ", " "),
      };
    case "store.reuse": {
      const business = clip(p.fromBusinessName ?? "your earlier store", 60);
      const offers = p.offers ?? 0;
      return {
        ...base,
        tone: "info",
        tab: "creatives",
        refresh: { ...none, creatives: true, properties: true, campaigns: true },
        text:
          offers > 0
            ? `You own ${p.address ?? "a new store"}. Re-use your store design and offers from ${business}?`
            : `You own ${p.address ?? "a new store"}. Re-use your store design from ${business}${p.fromAddress ? ` (${p.fromAddress})` : ""}?`,
        ...(p.propertyId ? { storePropertyId: p.propertyId, storeReuse: { offers } } : {}),
      };
    }
    case "campaign.reuse_offers":
      return {
        ...base,
        tone: "info",
        tab: "tasks",
        refresh: { ...none, campaigns: true },
        text: `You own ${p.address ?? "a new store"}. Re-use your offers from ${clip(p.fromBusinessName ?? "your earlier store", 60)}${p.fromAddress ? ` (${p.fromAddress})` : ""}?`,
        ...(p.propertyId ? { offersPropertyId: p.propertyId, reuseOffers: true } : {}),
      };
  }
}
